import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { showFullScreenLoading, tryHideFullScreenLoading } from "@/config/serviceLoading";
import { decode } from "@msgpack/msgpack";
//import msgpack from "msgpack-lite";
import { ElMessage } from "element-plus";
import { ResultEnum, example } from "@/enums/httpEnum";
import { calculateSHA3Encryption, calculateSHA3EncryptionWithParams } from "./processing";
import { useRouter } from "vue-router";
import { checkStatus } from "../helper/checkStatus";
import { useUserStore } from "@/stores/modules/user";
import request from "@/api/axios";
import qs from "qs";
// import { keepSpecifiedlocal } from "@/utils";
import { AUTH_SERVICE } from "@/api/config/servicePort";
import { ref } from "vue";
import { v4 as uuidv4 } from "uuid";

const onlycode = ref();
const copyonlycode = ref();

const getUniqueId = () => {
  return uuidv4();
};
const delonlycode = (obj: any) => {
  onlycode.value = null;
  copyonlycode.value = null;
  obj.headers.delete("onlycode");
};

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  noLoading?: boolean;
  isShowError?: boolean;
  keepAlive?: boolean;
}

export interface CustomAxiosResponse extends AxiosResponse {
  code?: Number;
  message?: String;
}

interface CustomAxiosRequestConfig2<D = any> extends AxiosRequestConfig<D> {
  noLoading?: boolean;
  isShowError?: boolean;
  keepAlive?: boolean;
}

type ExtendedAxiosInstance = AxiosInstance & {
  <T = any, R = AxiosResponse<T>, D = any>(config: CustomAxiosRequestConfig2<D>): Promise<R>;
  <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: CustomAxiosRequestConfig2<D>): Promise<R>;
};

const config = {
  // 默认地址请求地址，可在 .env.** 文件中修改
  baseURL: import.meta.env.VITE_API_URL as string,
  // 设置超时时间
  timeout: ResultEnum.TIMEOUT as number,
  // 跨域时候允许携带凭证
  withCredentials: true,
  headers: {} // 请求头信息
};

// 创建axios实例
const service: ExtendedAxiosInstance = axios.create(config);

/**
 * @description 请求拦截器
 * 客户端发送请求 -> [请求拦截器] -> 服务器
 * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
 */
service.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    // alert(config.url);
    const userStore = useUserStore();
    if (!onlycode.value) {
      onlycode.value = getUniqueId();
      config.headers.set("onlycode", onlycode.value);
    } else {
      if (copyonlycode.value === onlycode.value) return Promise.reject(new Error("请勿重复请求"));
    }
    // 生成随机小数
    // let randomNum = Math.floor(Math.random() * (99999999 - 1 + 1) + 1); // 生成一个八位数
    // config.data = {
    //   identification: randomNum,
    //   time_stamp: Date.parse(new Date()),
    //   data: config.data
    // };
    if (config.keepAlive == true) {
      config.timeout = 1000 * 60 * 30;
    }
    config.noLoading || showFullScreenLoading();
    if (userStore.token) {
      config.headers.set("Authorization", userStore.getAllToken);
    }

    if (config.method === "post" && (config.headers as any)["Content-Type"] === "application/x-www-form-urlencoded") {
      // console.log(config.data, " config.data config.data");
      config.data = qs.stringify(config.data);
    }
    if (config.method === "get") {
      // 压缩
      // config.headers.set("accept", "application/x-msgpack");
      // config.responseType = "arraybuffer";
    }
    // get参数编码
    if (config.method === "get" && config.params) {
      let url = config.url as string;
      url += "?";
      // config.params = Object.assign(
      //   {
      //     identification: randomNum,
      //     time_stamp: Date.parse(new Date())
      //   },
      //   config.params
      // );
      type stringKey = Record<string, any>;
      let params: stringKey = {};
      // let params = {} as object;
      const keys = Object.keys(config.params);
      for (const key of keys) {
        if (
          // 禁止提交的get参数类型
          config.params[key] !== void 0 &&
          config.params[key] !== null &&
          config.params[key] !== ""
        ) {
          let new_val = config.params[key];
          if (Array.isArray(new_val)) {
            new_val.forEach(v => {
              url += `${key}=${encodeURIComponent(v)}&`;
            });
            if (new_val.length > 0) {
              params[key] = encodeURIComponent(config.params[key]);
            }
          } else {
            new_val = encodeURIComponent(config.params[key]);
            url += `${key}=${new_val}&`;
            params[key] = new_val;
          }
        }
      }
      const encryption_data = calculateSHA3EncryptionWithParams(url, params);
      config.headers.set("X-SHA3-Hash", encryption_data);
      url = url.substring(0, url.length - 1);
      config.params = {};
      config.url = url;
    } else if (config.method != "get") {
      const encryption_data = calculateSHA3Encryption(config.data);
      config.headers.set("X-SHA3-Hash", encryption_data);
    }

    return config;
  },
  (error: AxiosError) => {
    delonlycode(config);
    return Promise.reject(error);
  }
);

/**
 * @description 响应拦截器
 *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
 */
service.interceptors.response.use(
  (response: CustomAxiosResponse) => {
    tryHideFullScreenLoading();
    if (response.config.responseType === "blob") {
      // 如果是文件流，直接过
      delonlycode(response.config);
      return response;
    }

    if (response.headers["content-type"] === "application/x-msgpack") {
      response.data = decode(response.data) as any;
    }

    const userStore = useUserStore();
    const code = response.data.code || ResultEnum.OVERDUE;
    const message = response.data.message ?? (response.message || "后端接口无返回内容");
    const refresh = response.headers["if-refresh"];
    // const version = response.headers["version"];
    // if (version !== userStore.userInfo["version"]) {
    //   userStore.userInfo["version"] = version;
    //   userStore.setUserInfo(userStore.userInfo);
    //   keepSpecifiedlocal("dev-user");
    // }

    if (code === ResultEnum.SUCCESS) {
      // 响应状态码200
      if (refresh === "1") {
        delonlycode(response.config);
        // 因token快过期，刷新token
        refreshToken().then(res => {
          userStore.setToken(res.data?.access_token);
          userStore.setTokenType(res.data?.token_type);
          userStore.setRefreshToken(res.data?.refresh_token);
        });
      }
      delonlycode(response.config);
      return response.data;
    } else if (code == ResultEnum.OVERDUE) {
      // 响应状态码401
      delonlycode(response.config);
      userStore.logout(message);
    } else {
      // if (response.config.isShowError) ElMessage.error(message);
      delonlycode(response.config);
      if (example[code]) return ElMessage.error(example[code]);
      return ElMessage.error(message);
    }
    response.code = code;
    return response;
  },
  async (error: AxiosError) => {
    const { response } = error;
    tryHideFullScreenLoading();
    // 请求超时 && 网络错误单独判断，没有 response
    if (error.message) {
      if (error.message.indexOf("Network Error") !== -1) {
        delonlycode(response.config);
        ElMessage.error("网络错误！请您稍后重试");
      }
      if (error.message.indexOf("timeout") !== -1) {
        ElMessage.error("请求超时！请您稍后重试");
        copyonlycode.value = JSON.parse(JSON.stringify(onlycode.value));
      }
      if (error.message.indexOf("请勿重复请求") !== -1) {
        ElMessage.error("请勿重复请求! 刷新页面重试");
      }
    }
    // 根据服务器响应的错误状态码，做不同的处理
    if (response) checkStatus(response.status);
    if (response.status >= 500) {
      const router = useRouter();
      router.push({
        path: "/500",
        query: {
          msg: "服务开小差去了~"
        }
      });
      return null;
    }
    // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
    //if (!window.navigator.onLine) router.push("/500");
    return Promise.reject(error);
  }
);

// 刷新Token
const refreshToken = (): Promise<IResponse> => {
  const userStore = useUserStore();
  const data = userStore.getRefreshToken;
  return request.post({ url: AUTH_SERVICE + "/token/refresh", data });
};

export { service };
